import { useMemo } from "react";

import { useTranslation } from "react-i18next";

import {
  CornerLeftUpIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { Loader } from "simplydo/react/ui/loader";
import { Message } from "simplydo/react/ui/message";
import { Popup } from "simplydo/react/ui/popup";

import util from "utils/utils";

import {
  annualImpactValue,
  generateImpactText,
  getImpactIconName,
  useImpactStageMap,
} from "./statementUtils";
import { useImpactStatements } from "./useImpactStatements";

type StageSegmentProps = {
  ideaId: string;
  stage: OpenAPI.Schemas["ImpactStatement"]["stage"];
  onView?: () => void;
  onCreated?: () => void;
};

export const StageSegment = ({
  ideaId,
  stage,
  onView,
  onCreated,
}: StageSegmentProps) => {
  const { t } = useTranslation();
  const {
    impactStatements,
    statementDefinition,
    isLoading,
    canEditProjectStages,
    creatingStage,
    createImpactStatement,
    deleteImpactStatement,
  } = useImpactStatements(ideaId);

  const impactStatement = impactStatements[stage];
  const segmentMap = useImpactStageMap();
  const def = segmentMap[stage as keyof typeof segmentMap];

  const visibleImpacts = useMemo(() => {
    if (!impactStatement?.impacts) {
      return [];
    }

    const impacts: (typeof impactStatement.impacts)[number][] = [];
    const impactOptions = statementDefinition?.impacts || {};
    for (const optionId in impactOptions) {
      const impactOption = impactOptions[optionId];
      if (impactOption?.enabled) {
        const impact = impactStatement.impacts[optionId];

        if (
          impact?.enabled &&
          impact?.ownerImpact &&
          // @ts-ignore
          (impact?.choice || impact?.value)
        ) {
          impacts.push(impact);
        }
      }
    }

    return impacts;
  }, [impactStatement, statementDefinition]);

  const getLabelStyle = (effect?: string) => {
    if (effect === "cost") {
      return { borderColor: "#f2b8b5", color: "#9f3a38" };
    }
    if (effect === "saving") {
      return { borderColor: "#c2e0c6", color: "#2c662d" };
    }
    return {};
  };

  const handleDelete = () => {
    if (stage === "userEstimate" || !impactStatement?._id) {
      return;
    }

    const confirmMessages = {
      projectEstimate: {
        title: "Delete the project estimate?",
        message:
          "Are you sure you want to delete the project estimate statement? This action is instant and irreversible.",
      },
      finalOutcome: {
        title: "Delete final outcome?",
        message:
          "Are you sure you want to delete the final outcome statement? This action is instant and irreversible.",
      },
    }[stage];

    util
      .confirm(confirmMessages.title, confirmMessages.message)
      .then(() => {
        deleteImpactStatement(stage, impactStatement._id);
      })
      .catch(() => {});
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Card fluid placeholder={!impactStatement}>
      <CardContent>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <H3>
              <Icon icon={def.icon} /> {def.name}
              <Subheader>{def.description}</Subheader>
            </H3>
          </div>
          {impactStatement ? (
            <>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 4,
                  flexWrap: "wrap",
                }}
              >
                {visibleImpacts.map((impact, index) => (
                  <Popup
                    key={index}
                    trigger={
                      <Label
                        basic
                        size="small"
                        style={{ ...getLabelStyle(impact.ownerImpact?.effect) }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                          }}
                        >
                          <div>
                            <Icon
                              icon={getImpactIconName(impact.ownerImpact?.type)}
                            />
                            {impact.ownerImpact?.effect === "cost" ? "-" : ""}
                            {annualImpactValue(impact)}
                          </div>
                          {impact.choice?.weight !== undefined ? (
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <Icon
                                icon={CornerLeftUpIcon}
                                style={{ marginRight: 2 }}
                              />
                              {impact.choice?.weight || 0}/
                              {impact.ownerImpact?.maxWeight || 0}
                            </div>
                          ) : null}
                        </div>
                      </Label>
                    }
                  >
                    {generateImpactText(impact, t)}
                  </Popup>
                ))}
              </div>
              <div style={{ display: "flex", flex: 1, marginTop: "1em" }}>
                <Button style={{ flex: 1 }} onClick={onView}>
                  <Icon icon={EyeIcon} />
                  View details
                </Button>
                {stage !== "userEstimate" && canEditProjectStages && (
                  <>
                    <Button style={{ flex: 1 }} onClick={onView}>
                      <Icon icon={PencilIcon} />
                      Edit Details
                    </Button>
                    <Button onClick={handleDelete} variant="outline">
                      <Icon icon={Trash2Icon} />
                    </Button>
                  </>
                )}
              </div>
            </>
          ) : def.create ? (
            canEditProjectStages ? (
              <Button
                onClick={() => {
                  createImpactStatement(stage, onCreated);
                }}
                loading={creatingStage === stage}
                color="secondary"
              >
                <Icon icon={PlusIcon} />
                {def.create}
              </Button>
            ) : (
              <Message>{def.name} has not been created yet.</Message>
            )
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};
